import { Fragment, useCallback, useState } from 'react'
import { ProductAddonsTableAndSearchDataStateSetData } from '../types'
import { ButtonOrLoader } from '../../../../../../../baseComponents/Button'
import { ModalOpenedTy, ModalOpenedSetTy } from '../../../../../../../baseComponents/Modal/types'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import FormRenderer from '../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import TwinForm from '../../../../../../../forms/TwinForm'
import { OnSubmit } from '../../../../../../../forms/TwinForm/types'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import useLangData from '../../../../../../../utils/hooks/useLangData'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveLangs } from '../../../../../../../utils/reducers/getters'
import { GroupVatJson } from '../../../../../../../utils/reducers/globals/groupVats'
import { changeErrorMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { ModifiedCreateProductModelType } from '../../../../../types'
import NewProductSpecificPricesTable from '../../../../../NewProductSpecificPricesTable'
import useModalCreateProductSpecificPricesLogic, { DataPricesTy, SetDataPricesTy } from '../../../../../../../utils/hooks/product/useModalCreateProductSpecificPricesLogic'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'

interface ModalCEPACreateFormProps extends ModalCEPACreateFormLogicBaseProps {
    groupVats: GroupVatJson
    title: string
}

const ModalCEPACreateForm: React.FC<ModalCEPACreateFormProps> = ({ groupVats, setData, title }) => {
    const { beforeSubmitAddSpecificPrices, dataPrices, setDataPrices, getDataPrices } = useModalCreateProductSpecificPricesLogic({})
    const { handleOnSubmit, openCreateProductAddon, setOpenCreateProductAddon, startLoading, loading, handleOnError } = useModalCEPACreateFormLogic({ setData, getDataPrices })
    return (
        <TwinForm action='/api/app/product/createProductAndGet' onSubmit={handleOnSubmit} beforeSubmitHandler={(vals) =>{ startLoading(); return beforeSubmitAddSpecificPrices(vals)}} onError={handleOnError} className='mb-30'>
            <ModalCEProductAddonHeader setOpenCreateProductAddon={setOpenCreateProductAddon} openCreateProductAddon={openCreateProductAddon} {...{title}} />
            {openCreateProductAddon ? <ProductAddonFormRender loading={loading} ProductTypeId={5} groupVats={groupVats} {...{ dataPrices, setDataPrices }} /> : null}
        </TwinForm>
    )
}

interface ModalCEPACreateFormLogicBaseProps {
    setData: ProductAddonsTableAndSearchDataStateSetData
}
interface ModalCEPACreateFormLogicProps extends ModalCEPACreateFormLogicBaseProps {
    getDataPrices: () => Promise<void>
}


const useModalCEPACreateFormLogic = ({ setData, getDataPrices }: ModalCEPACreateFormLogicProps) => {
    const { endLoading, loading, startLoading } = useIsLoading()
    const [openCreateProductAddon, setOpenCreateProductAddon] = useState<ModalOpenedTy>(null)
    const { t } = useTwinTranslation()
    const errorReference = t('referenceAlreadyExists', 'La referencia ya existe')

    const handleOnSubmit: OnSubmit = useCallback(async (res, values) => {
        if (res.status === 200) {
            const parsedRes = await res.json()
            setData((old) => {
                if (!old) {
                    return null
                }
                const copy = JSON.parse(JSON.stringify(old))
                copy.ids.push(parsedRes.id)
                copy.data.push(parsedRes)
                return {...copy}
            })
            setOpenCreateProductAddon(null)
        }
        await getDataPrices()
        endLoading()
    }, [setOpenCreateProductAddon, setData, endLoading, getDataPrices])

    const handleOnError: OnSubmit = useCallback(async (res: Response) => {
        const responseJSON = await res.json()
        if (responseJSON.errors[0].msg === 'This reference already exists') {
            changeErrorMessage(errorReference)
        } else {
            changeErrorMessage(responseJSON.errors[0].msg)
        }
        endLoading()
    }, [errorReference, endLoading])
    return { openCreateProductAddon, setOpenCreateProductAddon, handleOnSubmit, startLoading, loading, handleOnError }
}

interface ModalCEProductAddonHeaderProps {
    setOpenCreateProductAddon: ModalOpenedSetTy
    openCreateProductAddon: ModalOpenedTy
    title: string
}

const ModalCEProductAddonHeader: React.FC<ModalCEProductAddonHeaderProps> = ({ setOpenCreateProductAddon, openCreateProductAddon, title }) => {
    return (
        <div className='flex items-center'>
            <h2>{title}</h2>
            <span className='ml-auto text-16 text-green-21 cursor-pointer' onClick={() => setOpenCreateProductAddon((old) => old ? null : true)}>
                {openCreateProductAddon ? <TwinTrans transKey='close'>Cerrar</TwinTrans> : <TwinTrans transKey='createAddon'>Crear complemento</TwinTrans>}</span>
        </div>
    )
}


interface ProductAddonFormRenderProps {
    ProductTypeId: number
    groupVats: GroupVatJson
    loading: boolean
    dataPrices: DataPricesTy
    setDataPrices: SetDataPricesTy
}

const ProductAddonFormRender: React.FC<ProductAddonFormRenderProps> = ({ ProductTypeId, groupVats, loading, dataPrices, setDataPrices }) => {
    const { t } = useTwinTranslation()
    const { changeLang, selectedLang, data, changeValCurrentLang } = useLangData({ myData: null, fieldsToLang: ['name'], keyValue: 'LangId' })
    const { showPrices, setShowPrices } = useProductAddonFormRenderLogic()
    const fields: FormRender<ModifiedCreateProductModelType> = [
        {
            title: t('createNewAddon', 'Crear nuevo complemento'),
            cols: 2,
            elements: [
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                    value: selectedLang,
                    onChange: changeLang,
                    required: true
                },
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                    lang:selectedLang,
                    value: data?.['name'][selectedLang] ?? '',
                    onChange: (name) => changeValCurrentLang('name', name)
                },
                {
                    name: 'nameLangs',
                    component: 'InputHidden',
                    value: JSON.stringify(data?.name)
                },
                {
                    name: 'GroupVatId',
                    label: t('groupVat', 'Grupo de impuestos'),
                    component: 'CustomSelectWithSearchBar',
                    items: groupVats || {},
                    required: true
                },
                {
                    name: 'reference',
                    label: t('reference', 'referencia'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                },
                {
                    name: 'productType',
                    component: 'InputHidden',
                    value: ProductTypeId
                },
            ]
        }
    ]
    return (
        <Fragment>
            <FormRenderer sections={fields} className='mt-10'/>
            <div className='flex items-center my-20'>
                <span className='regular16 mr-10'><TwinTrans transKey='prices'>Precios</TwinTrans></span>
                <TwinIcon className='cursor-pointer' icon={showPrices ? faChevronUp : faChevronDown} onClick={() => setShowPrices(showPrices? null : true)}/>
            </div>
            {showPrices? <NewProductSpecificPricesTable {...{dataPrices, setDataPrices}} /> : null }
            <ButtonOrLoader buttonIsDisabled={loading} textButton={t('createAddon', 'Crear complemento')} className='mt-10' />
        </Fragment>
    )
}

const useProductAddonFormRenderLogic = () => {
    const [showPrices, setShowPrices] = useState<ModalOpenedTy>(null)

    return { showPrices, setShowPrices }
}

export default ModalCEPACreateForm